import { TFunction } from 'i18next';
import { ProductAggregation } from '../../orderhistory/types';
import { getCategoryDisplayName } from '../../orderhistory/categoryUtils';
import { buildTsv } from '../../../utils/tsvUtils';

/**
 * The flat product table behind the Products tab's exports: which rows, in which order, with
 * which columns. Deliberately free of ExcelJS so the clipboard action can import it statically —
 * awaiting a dynamic import before `clipboard.writeText` loses the user gesture in Safari.
 *
 * `productsExcelExport.ts` renders these same rows into the styled workbook.
 */

/**
 * Which shape of file the rows are for:
 *   'overview' → the internal sheet: article ids, prices and spend.
 *   'supplier' → the sheet you hand to a supplier to quote on: no article ids (they are the
 *                buyer's), no prices, no spend.
 */
export type ProductsFileMode = 'overview' | 'supplier';

/** One product row, already resolved to display values. */
export interface ExportRow {
  name: string;
  articleId: string;
  supplier: string;
  category: string;
  restaurants: string;
  quantity: string;
  unitPrice?: number;
  unit: string;
  spend: number;
}

type ColKey = keyof ExportRow;

export interface ColumnDef {
  key: ColKey;
  label: string;
  width: number;
}

// ---- Row mapping -------------------------------------------------------------

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Supplier label: the real supplier name for uploaded invoices, else the platform. */
function supplierLabel(product: ProductAggregation): string {
  if (product.platform === 'any_invoice' && product.unknownSupplierName) {
    return product.unknownSupplierName;
  }
  return capitalize(product.platform);
}

function categoryLabel(t: TFunction, product: ProductAggregation): string {
  return getCategoryDisplayName(product.category, product.mainCategory) ?? t('products.export.uncategorized');
}

export function toExportRow(t: TFunction, product: ProductAggregation): ExportRow {
  // `articleId` on the aggregation is the RESOLVED identifier — for name-keyed platforms
  // that is the product name itself, which is noise in an article-id column.
  const articleId = product.articleId && product.articleId !== product.name ? product.articleId : '';
  return {
    name: product.name,
    articleId,
    supplier: supplierLabel(product),
    category: categoryLabel(t, product),
    restaurants: (product.restaurantNames || []).join(', '),
    quantity: product.formattedQuantity || '',
    unitPrice: product.unitPrice?.value,
    unit: product.unitPrice?.unit ?? '',
    spend: product.spend,
  };
}

// ---- Table rendering ---------------------------------------------------------

export function buildColumns(
  t: TFunction,
  opts: { articleIds: boolean; supplier: boolean; category: boolean; restaurants: boolean; prices: boolean }
): ColumnDef[] {
  const cols: ColumnDef[] = [{ key: 'name', label: t('dashboard.product'), width: 40 }];
  if (opts.prices && opts.articleIds) cols.push({ key: 'articleId', label: t('products.export.colArticleId'), width: 16 });
  if (opts.prices && opts.supplier) cols.push({ key: 'supplier', label: t('orderHistory.supplierTableHeader'), width: 18 });
  if (opts.category) cols.push({ key: 'category', label: t('orderHistory.category'), width: 20 });
  if (opts.restaurants) cols.push({ key: 'restaurants', label: t('orderHistory.restaurants'), width: 24 });
  cols.push({ key: 'quantity', label: t('orderHistory.quantity'), width: 16 });
  // Supplier-facing files stop here: no prices, no spend.
  if (opts.prices) {
    cols.push(
      { key: 'unitPrice', label: t('orderHistory.unitPrice'), width: 14 },
      { key: 'unit', label: t('products.export.colUnit'), width: 10 },
      { key: 'spend', label: t('orderHistory.totalSpent'), width: 15 }
    );
  }
  return cols;
}

/** Biggest spenders first, then the top-N cap — the order and scope every export shares. */
export function rankAndCapProducts(products: ProductAggregation[], limit: number | null): ProductAggregation[] {
  const ranked = [...products].sort((a, b) => b.spend - a.spend);
  return limit != null ? ranked.slice(0, limit) : ranked;
}

export interface ProductsClipboardOptions {
  /** Internal ('overview') vs supplier-facing ('supplier'): the latter drops prices and spend. */
  fileMode: ProductsFileMode;
  /** Add a "Restaurants" column (only meaningful in the All Restaurants view). */
  includeRestaurants: boolean;
  /** Keep only the N biggest products by spend; null copies everything. */
  limit: number | null;
}

/**
 * The product table as tab-separated text, so it pastes straight into Excel / Google Sheets.
 *
 * One flat table rather than the workbook's per-supplier / per-category sheets: a clipboard
 * paste can only be one table, and keeping both the Supplier and Category columns lets the
 * receiver pivot it however they like. Row order, the top-N cap and the supplier-facing column
 * set are exactly the file's.
 */
export function buildProductsClipboardText(
  t: TFunction,
  products: ProductAggregation[],
  { fileMode, includeRestaurants, limit }: ProductsClipboardOptions
): string {
  const rows = rankAndCapProducts(products, limit).map((product) => toExportRow(t, product));
  const cols = buildColumns(t, {
    articleIds: rows.some((row) => row.articleId !== ''),
    supplier: true,
    category: true,
    restaurants: includeRestaurants,
    prices: fileMode === 'overview',
  });

  return buildTsv(
    cols.map((col) => col.label),
    rows.map((row) => cols.map((col) => {
      if (col.key === 'spend') return row.spend.toFixed(2);
      if (col.key === 'unitPrice') return row.unitPrice != null ? row.unitPrice.toFixed(2) : '';
      return row[col.key] as string;
    }))
  );
}
